import { useState, useEffect, useRef } from 'react';
import authRequest from '../utils/authRequest';
import NoteTemplatePresenter from '../presenters/NoteTemplatePresenter';

export default function useNoteTemplatePolling(uuid, pollIntervalMs = 10000) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const intervalIdRef = useRef(null);
  const lastUuidRef = useRef(uuid);
  const fetchInProgressRef = useRef(false);

  // Effect to handle polling logic
  useEffect(() => {
    isMountedRef.current = true;
    lastUuidRef.current = uuid;
    setLoading(false);

    // Clear any existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    async function loadTemplate() {
      // Don't start a new fetch if there's one in progress or component is unmounted
      if (fetchInProgressRef.current || !isMountedRef.current) return;
      
      // Don't fetch for stale UUIDs
      if (lastUuidRef.current !== uuid) return;
      
      try {
        fetchInProgressRef.current = true;
        setLoading(true);
        
        const response = await authRequest(`/note_templates/${uuid}`);
        if (response.status === 200) {
          const templatePresenter = new NoteTemplatePresenter(response.data);
          
          // After fetch completes, check if still mounted and the UUID is still current
          if (!isMountedRef.current || lastUuidRef.current !== uuid) {
            fetchInProgressRef.current = false;
            return;
          }

          setTemplate(templatePresenter);
          setError(null);
          fetchInProgressRef.current = false;

          // If template is ready, stop polling
          if (templatePresenter && templatePresenter.isReady()) {
            stopPolling();
            setLoading(false);
          } 
          // Otherwise start polling if not already started
          else if (!intervalIdRef.current && isMountedRef.current) {
            startPolling();
          }
        }
      } catch (err) {
        // After error, check if still mounted and the UUID is still current
        if (!isMountedRef.current || lastUuidRef.current !== uuid) {
          fetchInProgressRef.current = false;
          return;
        }
        
        setError(err);
        setLoading(false);
        fetchInProgressRef.current = false;
        stopPolling();
      }
    }

    function startPolling() {
      if (intervalIdRef.current) return;
      
      intervalIdRef.current = setInterval(() => {
        // Only continue polling if component mounted and UUID is current
        if (isMountedRef.current && lastUuidRef.current === uuid) {
          loadTemplate();
        } else {
          stopPolling();
        }
      }, pollIntervalMs);
    }

    function stopPolling() {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }

    // Only start polling if UUID is provided
    if (uuid) {
      loadTemplate();
    }

    // Cleanup on unmount or uuid change
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [uuid, pollIntervalMs]);

  return { template, loading, error };
}
